import { PlayerRosterV2 } from '@wwe-portal/entities/roster.type';
import { RosterCardData } from '@wwe-portal/ui/features/roster/roster-card/roster-card-v2';
import { extractSuperstarName } from './extractor';
import { Superstar } from '@wwe-portal/entities/superstar.type';
import { RosterAvatarProps } from '@wwe-portal/ui/features/roster/roster-avatar';
import { RosterStarData } from '@wwe-portal/ui/features/roster/roster-star/roaster-star';
import { TrendingCardData } from '@wwe-portal/ui/features/roster/roster-trending-carousel';
import { getYoutubeEmbedLink } from '@wwe-portal/utils/link';
import { isSuperstarLevel } from './helper-v2';

export const fullStar: RosterStarData = {
  level: 'G',
  rate: 6,
  hollowStar: 0,
};

const isUserNotOwnedSuperstar = (
  playerRoster: Superstar,
  options: {
    mySuperstars: Map<string, PlayerRosterV2> | undefined;
    defaultStar: RosterStarData | undefined;
    isAuthenticated: boolean;
  }
) => {
  // Skip check if user is not authenticated or mySuperstars is not provided
  if (!options.isAuthenticated || options.mySuperstars == null) return false;
  const mySuperstar = options.mySuperstars.get(playerRoster.groupId);
  return mySuperstar == null;
};

interface SuperstarMapperOptions {
  mySuperstars: Map<string, PlayerRosterV2> | undefined;
  defaultStar: RosterStarData | undefined;
  isAuthenticated: boolean;
}

export const mapPlayerRosterToRosterCardData = (
  playerRoster: Superstar,
  options: SuperstarMapperOptions
) => {
  const mainString = playerRoster.name
    ? extractSuperstarName(playerRoster.name)?.mainString
    : '';
  const result: RosterCardData = {
    id: playerRoster.groupId,
    title: mainString,
    image: playerRoster.image,
    tier: playerRoster.tier ?? '',
    talent: getCurrentSuperstarTalent(playerRoster, options),
    star: getSuperstarStar(playerRoster, options),
    notOwned: isUserNotOwnedSuperstar(playerRoster, options),
    fullName: playerRoster.name,
    winPercentage: playerRoster.winPercentage,
    totalUpgrades: playerRoster.totalUpgrades,
  };
  return result;
};

export const mapPlayerRosterToTrendingCardData = (
  playerRoster: Superstar,
  options: SuperstarMapperOptions
) => {
  const result: TrendingCardData = {
    ...mapPlayerRosterToRosterCardData(playerRoster, options),
    // Show full star and talent for trending card
    star: fullStar,
    talent: playerRoster.talent,
    videoLinks: playerRoster.videoLinks?.map((item) => ({
      image: item.image.trim(),
      link: getYoutubeEmbedLink(item.link.trim()),
    })),
  };
  return result;
};

export const mapSuperstarToAvatarProps = (
  superstar: Superstar,
  options: {
    mySuperstars: Map<string, PlayerRosterV2> | undefined;
    defaultStar: RosterStarData | undefined;
    isAuthenticated: boolean;
  }
) => {
  const avatarProps: RosterAvatarProps = {
    src: superstar.avatarSS ?? '',
    tier: superstar.tier,
    star: getSuperstarStar(superstar, options),
    notOwned: isUserNotOwnedSuperstar(superstar, options),
  };
  return avatarProps;
};

export const getCurrentSuperstarTalent = (
  superstar: Superstar,
  options: Pick<SuperstarMapperOptions, 'mySuperstars' | 'isAuthenticated'>
) => {
  const mySuperstar = options.mySuperstars?.get(superstar.groupId);
  if (!options.isAuthenticated || !mySuperstar) {
    return superstar.talent;
  }
  return mySuperstar.talent;
};

export const getSuperstarStar = (
  superstar: Superstar,
  options: SuperstarMapperOptions
) => {
  const mySuperstar = options.mySuperstars?.get(superstar.groupId);
  const level = mySuperstar?.level;

  if (!options.isAuthenticated || !mySuperstar || !isSuperstarLevel(level)) {
    return options.defaultStar;
  }
  const result: RosterStarData = {
    level,
    rate: mySuperstar.solidStars,
    hollowStar: mySuperstar.hollowStars,
  };
  return result;
};
